// Bilingual copy — reviewed and rewritten by a copywriter.
// Every line aims to sound human, calm, and confident.
export const translations = {
  en: {
    brand: "The Life Plot",
    tagline: "Write your next chapter.",
    mission:
      "We help people change the direction of their lives through short, practical guides that turn intention into action.",
    nav: {
      guides: "Guides",
      assessment: "Assessment",
      blog: "Journal",
      contact: "Contact",
      dashboard: "Account",
      login: "Sign in",
      register: "Create account",
      logout: "Sign out",
    },
    home: {
      eyebrow: "Practical · No theatre",
      heroLine1: "Your life has a plot.",
      heroLine2: "You can rewrite it.",
      heroSub:
        "Short, deliberate guides — written for people who are done reading and ready to act. Begin in three minutes.",
      ctaPrimary: "Start your next chapter",
      ctaSecondary: "Browse the guides",
      howKicker: "How it works",
      howTitle: "Four steps. Repeat for life.",
      howSub:
        "Every guide is stripped down to its working parts — no theory dumps, no decade-long retreats. Just the next move, written on a page you can finish in twenty minutes.",
      howSteps: [
        { t: "Choose your challenge", d: "Pick the chapter you want to rewrite — confidence, money, discipline, or any of the twelve." },
        { t: "Download your guide", d: "Receive a beautifully designed PDF, written for action, not theory." },
        { t: "Take action", d: "Follow a 7-day protocol in 20-minute sessions — built to fit a real life." },
        { t: "Transform your story", d: "Track streaks, unlock achievements, and watch the new chapter compound week over week." },
      ],
      categoriesKicker: "The library",
      categoriesTitle: "Choose your chapter.",
      categoriesSub:
        "Twelve practical paths. Each one ends with the same word: action.",
      assessmentKicker: "Personal · 3 minutes",
      assessmentTitle: "Not sure where to begin?",
      assessmentSub:
        "Answer twelve honest questions. Receive a personal reading of your current chapter, and the three guides designed to move it forward.",
      assessmentCta: "Take the Life Plot Assessment",
      assessmentBullets: [
        "Twelve honest questions",
        "AI-assisted personal reading",
        "Three guides matched to you",
      ],
      authorKicker: "Why we exist",
      authorTitle: "Not another ebook store.",
      authorBody:
        "Every guide here exists for one reason: to make the next thirty days of your life measurably different from the last thirty. We write the way a friend who has already done it would write to you — without filler, without lectures.",
      reviewsKicker: "Field reports",
      reviewsTitle: "Readers, in their own words.",
      finalCtaKicker: "Begin today",
      finalCtaTitle: "Your story isn't waiting.",
      finalCtaSub: "Spend three minutes on the assessment. The first move is closer than you think.",
    },
    categoryLabel: "Category",
    guide: {
      backLink: "All guides",
      readingTime: "Reading time",
      minutes: "min",
      whatsInside: "What's inside",
      preview: "Preview",
      exercises: "Exercises",
      actionPlan: "Action plan",
      checklist: "Checklist",
      faq: "Frequently asked",
      testimonials: "What readers say",
      purchase: "Get the guide",
      currency: "EUR",
      lifetime: "One-time. Lifetime access.",
      includes: [
        "Beautiful PDF — EN + RO",
        "Apple Pay, Google Pay, cards",
        "Instant email delivery",
      ],
    },
    assessment: {
      title: "The Life Plot Assessment",
      sub: "Twelve questions. One personal reading. Zero hype.",
      start: "Begin assessment",
      question: "Question",
      of: "of",
      next: "Next",
      prev: "Back",
      submit: "Generate my reading",
      loading: "Reading your chapter…",
      reportKicker: "Your reading",
      reportTitle: "Your reading is in.",
      strengths: "Strengths",
      growth: "Growth areas",
      recommended: "Guides built for you",
      nextChapter: "Your next chapter",
      retake: "Take it again",
      placeholder: "Type your honest answer…",
    },
    blog: {
      kicker: "The journal",
      title: "Field notes.",
      sub: "Short essays on rewriting your life — one paragraph at a time.",
      readMore: "Read",
      minRead: "min read",
    },
    contact: {
      kicker: "Talk to us",
      title: "We read every message.",
      sub: "Questions, partnerships, or feedback — write to us directly and a human will reply within one business day.",
      name: "Your name",
      email: "Your email",
      message: "Your message",
      send: "Send message",
      sent: "Thanks — your message is in. We'll reply soon.",
      direct: "Or write to us directly:",
    },
    newsletter: {
      kicker: "The Life Plot letter",
      title: "One essay a week.",
      sub: "Short, useful, sent on Sunday. Unsubscribe whenever.",
      placeholder: "you@example.com",
      submit: "Subscribe",
      success: "Check your inbox — confirm with one click to finish.",
    },
    auth: {
      loginTitle: "Welcome back.",
      loginSub: "Pick up where you left off.",
      registerTitle: "Create your account.",
      registerSub: "Start tracking your next chapter.",
      forgotTitle: "Reset your password",
      forgotSub: "Enter your email and we'll send a secure reset link.",
      resetTitle: "Set a new password",
      resetSub: "Use 6 characters or more. You'll be signed in after.",
      forgotLink: "Forgot password?",
      checkInbox: "If that email exists, a reset link is on its way.",
      email: "Email address",
      password: "Password",
      newPassword: "New password",
      name: "Your full name",
      submitLogin: "Sign in",
      submitRegister: "Create account",
      submitForgot: "Send reset link",
      submitReset: "Set new password",
      switchToRegister: "New here? Create an account",
      switchToLogin: "Already have an account? Sign in",
    },
    dashboard: {
      kicker: "Your account",
      welcome: "Welcome back,",
      streak: "Day streak",
      purchased: "Your guides",
      wishlist: "Saved for later",
      achievements: "Achievements",
      noGuides: "You haven't started a chapter yet.",
      browseGuides: "Browse guides",
      emptyWishlist: "Nothing saved yet.",
      noAch: "Take the assessment or open a guide to earn your first badge.",
    },
    checkout: {
      successTitle: "Your chapter begins now.",
      successSub: "Your guide and download link are on their way to your inbox. Check your spam folder if you don't see it within five minutes.",
      successSubGuest: "Your guide and download link are on the way. Use the button below to download immediately.",
      cancelTitle: "Checkout cancelled.",
      cancelSub: "No charge was made. You can come back any time.",
      checking: "Confirming your payment…",
      downloadNow: "Download now",
      orderId: "Order",
      goDashboard: "Open dashboard",
      backToGuides: "Back to guides",
    },
    legal: {
      privacyTitle: "Privacy",
      privacyIntro:
        "We respect your privacy. This page describes what we collect, why we collect it, and what we do with it.",
      termsTitle: "Terms",
      termsIntro:
        "By using The Life Plot you agree to the terms below. They are written in plain English — read them before you buy.",
      updated: "Last updated",
    },
    footer: {
      tag: "Write your next chapter.",
      explore: "Explore",
      company: "Company",
      legal: "Legal",
      about: "About",
      contact: "Contact",
      privacy: "Privacy",
      terms: "Terms",
      rights: "All rights reserved.",
      newsletterTitle: "Get the letter.",
      newsletterSub: "One essay a week. Sent on Sunday. Unsubscribe whenever.",
    },
    common: {
      back: "Back",
      loading: "Loading…",
      tryAgain: "Try again",
      sending: "Sending…",
    },
  },
  ro: {
    brand: "The Life Plot",
    tagline: "Scrie următorul capitol al vieții tale.",
    mission:
      "Ne ajutăm cititorii să schimbe direcția vieții lor prin ghiduri scurte, practice, care transformă intenția în acțiune.",
    nav: {
      guides: "Ghiduri",
      assessment: "Evaluare",
      blog: "Jurnal",
      contact: "Contact",
      dashboard: "Cont",
      login: "Autentificare",
      register: "Creează cont",
      logout: "Deconectare",
    },
    home: {
      eyebrow: "Practic · Fără teatru",
      heroLine1: "Viața ta are un fir narativ.",
      heroLine2: "Îl poți rescrie.",
      heroSub:
        "Ghiduri scurte și deliberate — scrise pentru cei care au terminat cu cititul și sunt pregătiți să acționeze. Începe în trei minute.",
      ctaPrimary: "Începe următorul capitol",
      ctaSecondary: "Descoperă ghidurile",
      howKicker: "Cum funcționează",
      howTitle: "Patru pași. Repetabili la nesfârșit.",
      howSub:
        "Fiecare ghid este redus la părțile sale esențiale — fără teorii nesfârșite, fără retrageri de un deceniu. Doar următoarea mișcare, scrisă pe o pagină pe care o termini în douăzeci de minute.",
      howSteps: [
        { t: "Alege provocarea", d: "Alege capitolul pe care vrei să-l rescrii — încredere, bani, disciplină sau oricare dintre cele douăsprezece." },
        { t: "Descarcă ghidul", d: "Primești un PDF gândit pentru acțiune, nu pentru teorie." },
        { t: "Aplică pașii", d: "Urmează un protocol de 7 zile, în reprize de 20 de minute — construit pentru o viață reală." },
        { t: "Transformă-ți povestea", d: "Urmărește seriile, deblochează realizări și vezi cum noul capitol crește săptămână de săptămână." },
      ],
      categoriesKicker: "Biblioteca",
      categoriesTitle: "Alege capitolul tău.",
      categoriesSub:
        "Douăsprezece căi practice. Fiecare se termină cu același cuvânt: acțiune.",
      assessmentKicker: "Personal · 3 minute",
      assessmentTitle: "Nu știi de unde să începi?",
      assessmentSub:
        "Răspunde la douăsprezece întrebări sincere. Primești o lectură personală a capitolului tău actual și cele trei ghiduri gândite să te ducă mai departe.",
      assessmentCta: "Începe evaluarea Life Plot",
      assessmentBullets: [
        "Douăsprezece întrebări sincere",
        "Lectură personală asistată de AI",
        "Trei ghiduri potrivite pentru tine",
      ],
      authorKicker: "De ce existăm",
      authorTitle: "Nu este încă un magazin de ebook-uri.",
      authorBody:
        "Fiecare ghid de aici există dintr-un singur motiv: să facă următoarele treizeci de zile măsurabil diferite de ultimele treizeci. Scriem așa cum ți-ar scrie un prieten care a trecut deja prin asta — fără umplutură, fără lecții.",
      reviewsKicker: "Rapoarte de teren",
      reviewsTitle: "Cititorii, cu propriile cuvinte.",
      finalCtaKicker: "Începe azi",
      finalCtaTitle: "Povestea ta nu te așteaptă.",
      finalCtaSub: "Trei minute pentru evaluare. Prima mișcare este mai aproape decât crezi.",
    },
    categoryLabel: "Categorie",
    guide: {
      backLink: "Toate ghidurile",
      readingTime: "Timp de citire",
      minutes: "min",
      whatsInside: "Ce conține",
      preview: "Previzualizare",
      exercises: "Exerciții",
      actionPlan: "Plan de acțiune",
      checklist: "Listă de verificare",
      faq: "Întrebări frecvente",
      testimonials: "Ce spun cititorii",
      purchase: "Cumpără ghidul",
      currency: "EUR",
      lifetime: "O singură plată. Acces pe viață.",
      includes: [
        "PDF rafinat — EN + RO",
        "Apple Pay, Google Pay, carduri",
        "Livrare instant pe email",
      ],
    },
    assessment: {
      title: "Evaluarea The Life Plot",
      sub: "Douăsprezece întrebări. O lectură personală. Zero clișee.",
      start: "Începe evaluarea",
      question: "Întrebarea",
      of: "din",
      next: "Mai departe",
      prev: "Înapoi",
      submit: "Generează lectura mea",
      loading: "Îți citim capitolul…",
      reportKicker: "Lectura ta",
      reportTitle: "Lectura ta este gata.",
      strengths: "Puncte forte",
      growth: "Zone de creștere",
      recommended: "Ghiduri pentru tine",
      nextChapter: "Următorul tău capitol",
      retake: "Reia evaluarea",
      placeholder: "Scrie un răspuns sincer…",
    },
    blog: {
      kicker: "Jurnalul",
      title: "Note de teren.",
      sub: "Eseuri scurte despre cum să-ți rescrii viața — un paragraf pe rând.",
      readMore: "Citește",
      minRead: "min de citit",
    },
    contact: {
      kicker: "Scrie-ne",
      title: "Citim fiecare mesaj.",
      sub: "Întrebări, parteneriate sau păreri — scrie-ne direct și un om îți va răspunde în maximum o zi lucrătoare.",
      name: "Numele tău",
      email: "Email-ul tău",
      message: "Mesajul tău",
      send: "Trimite mesajul",
      sent: "Mulțumim — mesajul tău a fost trimis. Răspundem în curând.",
      direct: "Sau scrie-ne direct la:",
    },
    newsletter: {
      kicker: "Scrisoarea Life Plot",
      title: "Un eseu pe săptămână.",
      sub: "Scurt, util, trimis duminica. Te poți dezabona oricând.",
      placeholder: "tu@exemplu.com",
      submit: "Abonează-te",
      success: "Verifică-ți inbox-ul — confirmă cu un click pentru a finaliza.",
    },
    auth: {
      loginTitle: "Bine ai revenit.",
      loginSub: "Continuă de unde ai rămas.",
      registerTitle: "Creează-ți contul.",
      registerSub: "Începe să-ți urmărești următorul capitol.",
      forgotTitle: "Resetează parola",
      forgotSub: "Introdu emailul și îți trimitem un link sigur pentru resetare.",
      resetTitle: "Setează o parolă nouă",
      resetSub: "Folosește 6 caractere sau mai multe. Vei fi autentificat după.",
      forgotLink: "Ai uitat parola?",
      checkInbox: "Dacă acel email există, linkul de resetare este pe drum.",
      email: "Adresă de email",
      password: "Parolă",
      newPassword: "Parolă nouă",
      name: "Numele tău complet",
      submitLogin: "Autentifică-te",
      submitRegister: "Creează cont",
      submitForgot: "Trimite linkul",
      submitReset: "Setează parola",
      switchToRegister: "Nou aici? Creează un cont",
      switchToLogin: "Ai deja cont? Autentifică-te",
    },
    dashboard: {
      kicker: "Contul tău",
      welcome: "Bine ai revenit,",
      streak: "Zile consecutive",
      purchased: "Ghidurile tale",
      wishlist: "Salvate pentru mai târziu",
      achievements: "Realizări",
      noGuides: "Nu ai început încă un capitol.",
      browseGuides: "Descoperă ghidurile",
      emptyWishlist: "Nimic salvat încă.",
      noAch: "Începe evaluarea sau deschide un ghid pentru a-ți câștiga prima insignă.",
    },
    checkout: {
      successTitle: "Capitolul tău începe acum.",
      successSub: "Ghidul tău și linkul de descărcare sunt pe drum spre inbox-ul tău. Verifică folderul de spam dacă nu îl vezi în cinci minute.",
      successSubGuest: "Ghidul tău și linkul de descărcare sunt pe drum. Folosește butonul de mai jos pentru descărcare imediată.",
      cancelTitle: "Plata a fost anulată.",
      cancelSub: "Nu a fost efectuată nicio plată. Te poți întoarce oricând.",
      checking: "Confirmăm plata ta…",
      downloadNow: "Descarcă acum",
      orderId: "Comandă",
      goDashboard: "Deschide contul",
      backToGuides: "Înapoi la ghiduri",
    },
    legal: {
      privacyTitle: "Confidențialitate",
      privacyIntro:
        "Îți respectăm intimitatea. Această pagină descrie ce colectăm, de ce și ce facem cu informațiile.",
      termsTitle: "Termeni",
      termsIntro:
        "Folosind The Life Plot accepți termenii de mai jos. Sunt scriși în română simplă — citește-i înainte să cumperi.",
      updated: "Actualizat",
    },
    footer: {
      tag: "Scrie următorul capitol.",
      explore: "Explorează",
      company: "Companie",
      legal: "Legal",
      about: "Despre",
      contact: "Contact",
      privacy: "Confidențialitate",
      terms: "Termeni",
      rights: "Toate drepturile rezervate.",
      newsletterTitle: "Primește scrisoarea.",
      newsletterSub: "Un eseu pe săptămână. Trimis duminica. Te dezabonezi oricând.",
    },
    common: {
      back: "Înapoi",
      loading: "Se încarcă…",
      tryAgain: "Încearcă din nou",
      sending: "Se trimite…",
    },
  },
};

export const LANGS = ["en", "ro"];
